/**
 * Bidirectional helpers for Word `insertText`.
 *
 * Word (esp. on Mac) applies Unicode glyph mirroring to `[](){}` inside RTL runs, so a logical
 * `[הערה]` displays as `]הערה[`. HTML previews with `dir=rtl` do NOT mirror those glyphs, which is
 * why the preview card looks correct while the inserted Word text looks flipped.
 *
 * The legacy path never hit this because it inserted OOXML that carried the run's original
 * `w:rtl`/complex-script properties. The hybrid path uses native `insertText`, which creates a
 * plain run with no such properties, so Word mirrors the brackets.
 *
 * Fix: for RTL text we pre-swap the paired punctuation before inserting. Word's own mirroring then
 * paints the glyphs the user expects. LTR text (English, etc.) is returned untouched.
 *
 * Tradeoff (intentional): the stored codepoint for a bracket is its mirror. Every RTL-aware viewer
 * (Word Win/Mac, Google Docs, browsers with `dir=rtl`) mirrors it back, so it renders correctly.
 * Only naive plain-text extraction that ignores bidi would see the swapped char. Keeping the real
 * characters instead would require inserting an RTL OOXML run (see `word/utils/ooxmlRtl.ts`), which
 * reintroduces the `insertOoxml` fragility the hybrid engine was built to avoid.
 */

import { detectTextDirection } from './textUtils';

/** Hebrew, Arabic, and related presentation forms. */
const RTL_SCRIPT_RE =
  /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/** Each mirrored punctuation char mapped to the glyph Word renders in the opposite direction. */
const MIRROR: Record<string, string> = {
  '[': ']',
  ']': '[',
  '(': ')',
  ')': '(',
  '{': '}',
  '}': '{',
};

const PAIRED_PUNCT_RE = /[[\](){}]/g;

/** True if the string contains any RTL-script letter (Hebrew/Arabic/etc.). */
export function containsRtlScript(text: string): boolean {
  return !!text && RTL_SCRIPT_RE.test(text);
}

/** True when meaningful content is predominantly RTL (same rule as `detectTextDirection`). */
export function isPrimarilyRtl(text: string): boolean {
  return detectTextDirection(text) === 'rtl';
}

/** True when the segment is only whitespace/punctuation (no letters or digits). */
export function isPunctuationOnly(text: string): boolean {
  if (!text) return false;
  return /^[\s\[\](){}.,;:!?'"״׳\-–—/\\]+$/u.test(text);
}

/**
 * Whether a Word/OOXML text run should carry `w:rtl`.
 * RTL-script runs always; punctuation-only runs inherit when the paragraph is RTL
 * (so brackets/quotes stay with the surrounding Hebrew/Arabic).
 */
export function runNeedsRtl(text: string, paragraphIsRtl = false): boolean {
  if (containsRtlScript(text)) return true;
  if (paragraphIsRtl && isPunctuationOnly(text)) return true;
  return false;
}

/**
 * Strip trailing paragraph control chars Word adds to Range.text (e.g. `\r`) that are not part of
 * the visible clause content and would otherwise produce spurious diff insertions.
 */
export function normalizeWordParagraphBaseline(text: string): string {
  return text.replace(/[\r\n\u000b\f]+$/, '');
}

/**
 * Prepare text for Word `insertText` when the target run carries RTL/complex-script properties
 * (the legacy `insertOoxml`/replace-range path). Word glyph-mirrors paired punctuation `[](){}` in
 * such runs, so we pre-swap the logical characters and let Word mirror them back on display.
 *
 * NOTE: the hybrid engine does NOT use this — its native `insertText` creates plain (non-RTL) runs
 * that Word does not mirror, so it inserts the logical text unchanged. Callers must pass logical
 * (source) text; applying twice swaps the brackets back.
 */
export function prepareBidiTextForWordInsert(text: string): string {
  if (!text || detectTextDirection(text) !== 'rtl') return text;
  return text.replace(PAIRED_PUNCT_RE, (ch) => MIRROR[ch] ?? ch);
}
